import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, Ruler } from 'lucide-react';
import { measureGuide } from '../../content/measureGuide';
import { isBoreSwap } from '../../lib/lockFit';
import BacksetDiagram from './BacksetDiagram';

// The backset, asked on a shipped cart line once the lock is chosen (Hugh, 22 Sep 2026) —
// never on the product page, never on a line we fit (the size is part of the booking process,
// off the door photos) and never for a line that came from Find my lock. A row of size chips,
// and a "What's my backset?" button that opens the how-to for this door right here: a picture,
// where the tape goes and the usual sizes. `nudge` is set when Pay is pressed without a size.
function guideFor(product, material) {
  const keys = measureGuide.doors.map((d) => d.key);
  const pick = (k) => measureGuide.doors[keys.indexOf(k)];
  if (product.fit.sliding) return pick('sliding');
  if (isBoreSwap(product)) return pick('round-bore');
  return pick(material === 'aluminium' ? 'aluminium-hinged' : 'timber-mortice');
}

const chip = (on) => `rounded-full border-2 px-3 py-1.5 text-sm font-semibold leading-tight transition-colors ${on ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'}`;

export default function BacksetPicker({ product, value, onChange, nudge = false }) {
  const sizes = product.fit.backsetsMm;
  const sliding = !!product.fit.sliding;
  const adjustable = isBoreSwap(product);
  const [help, setHelp] = useState(false);
  const fitsBoth = product.fit.doorMaterials.includes('aluminium') && product.fit.doorMaterials.includes('timber');
  const [material, setMaterial] = useState(product.fit.doorMaterials.includes('aluminium') ? 'aluminium' : 'timber');
  const ref = useRef(null);
  useEffect(() => {
    if (nudge && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [nudge]);

  const guide = guideFor(product, material);
  const label = sliding ? 'Mortice size' : 'Backset';

  return (
    <div ref={ref} className={`mt-3 rounded-lg border-2 p-3 transition-colors ${nudge && !value ? 'border-red-500 bg-red-50/40' : value ? 'border-gray-200' : 'border-blue-300 bg-blue-50/40'}`} data-testid="backset-picker">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <p className="text-sm font-bold text-gray-900">{label}</p>
        {value ? (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-800" data-testid="backset-chosen">{value} mm</span>
        ) : (
          <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-600 text-white" data-testid="backset-required">Choose one</span>
        )}
        <span className="text-xs text-gray-600">door edge to the centre of the {adjustable ? 'hole' : 'keyhole'}{adjustable ? ' · the latch adjusts to either' : ''}</span>
      </div>

      <div className="flex flex-wrap gap-2 mt-2" role="radiogroup" aria-label={label} data-testid="backset-options">
        {sizes.map((b) => (
          <button key={b} type="button" role="radio" aria-checked={value === b} aria-label={`${b} mm`} onClick={() => onChange(b)} className={chip(value === b)} data-testid={`backset-${b}`}>
            {b} mm
          </button>
        ))}
        <button type="button" onClick={() => setHelp((h) => !h)} aria-expanded={help} className="inline-flex items-center gap-1 rounded-full border-2 border-gray-900 px-3 py-1.5 text-sm font-bold text-gray-900 hover:bg-gray-900 hover:text-white" data-testid="backset-help-toggle">
          <Ruler size={14} /> What's my {label.toLowerCase()}? <ChevronDown size={14} className={`transition-transform ${help ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {nudge && !value && (
        <p className="text-sm font-semibold text-red-700 mt-2" role="alert" data-testid="backset-nudge">
          Choose the {label.toLowerCase()} — the lock comes cut to it.
        </p>
      )}

      {help && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3" data-testid="backset-help">
          {fitsBoth && !sliding && (
            <div className="flex flex-wrap items-center gap-2 mb-3 text-sm">
              <span className="text-gray-600">Your door:</span>
              {[['aluminium', 'Aluminium'], ['timber', 'Timber']].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setMaterial(k)} className={`rounded-full border px-3 py-1 ${material === k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500'}`} aria-pressed={material === k}>{l}</button>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-2 gap-4 items-start">
            {guide.img ? (
              <img src={guide.img} alt={guide.imgAlt} className="w-full rounded-lg object-cover aspect-[4/3] bg-gray-100" loading="lazy" />
            ) : (
              <BacksetDiagram kind={guide.diagram} className="rounded-lg border border-gray-200 p-2" />
            )}
            <div className="text-sm space-y-2">
              <p className="font-bold text-gray-900">{guide.title}</p>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Tape:</span> {guide.tape}</p>
              <p className="text-gray-700"><span className="font-semibold text-gray-900">Usual sizes:</span> {guide.sizes}</p>
              <p className="text-xs text-gray-600">{guide.tip}</p>
              <p className="text-gray-600">Fitted by us? Tick supply &amp; install — nothing to measure.</p>
              <Link to="/shop/how-to-measure" className="text-red-600 font-semibold inline-flex items-center gap-1">Full guide, every door <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
